import { ChevronLeft, MapPin, PackageCheck, Truck } from "lucide-react";
import { useNavigate } from "react-router-dom";

const EFFECTIVE_DATE = "12 August 2026";
const UPDATED_DATE = "12 August 2026";

export default function ShippingPolicyPage() {
  const navigate = useNavigate();

  return (
    <main className="min-h-screen bg-[#fcfaf9] pb-16">
      <section className="border-b border-[#eadde1] bg-white">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 transition hover:text-primary-800"
          >
            <ChevronLeft size={17} />
            Back
          </button>

          <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_auto] lg:items-end">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary-800">
                CHARMS Policies
              </p>

              <h1 className="mt-3 text-4xl font-semibold tracking-tight text-slate-950 sm:text-5xl">
                Shipping Policy
              </h1>

              <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-600 sm:text-base">
                Everything you need to know about order confirmation,
                processing, delivery, tracking and shipping support.
              </p>
            </div>

            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-800 text-white shadow-lg shadow-primary-900/20">
              <Truck size={27} />
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 border-t border-[#eadde1] pt-5 text-xs font-medium text-slate-500">
            <span>Effective date: {EFFECTIVE_DATE}</span>
            <span>Last updated: {UPDATED_DATE}</span>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8 lg:py-14">
        <div className="grid gap-8 lg:grid-cols-[260px_minmax(0,1fr)]">
          <aside className="h-fit rounded-2xl border border-[#eadde1] bg-white p-5 lg:sticky lg:top-48">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-primary-800">
              Quick summary
            </p>

            <div className="mt-5 space-y-5">
              <SummaryItem
                icon={<MapPin size={17} />}
                title="Serviceable PIN codes"
                text="Delivery availability is confirmed at checkout."
              />

              <SummaryItem
                icon={<PackageCheck size={17} />}
                title="Prepaid orders"
                text="Orders are confirmed only after payment verification."
              />

              <SummaryItem
                icon={<Truck size={17} />}
                title="Tracking updates"
                text="Tracking details are shared after dispatch where available."
              />
            </div>

            <div className="mt-6 border-t border-[#eadde1] pt-5">
              <button
                type="button"
                onClick={() => navigate("/refundpolicy")}
                className="text-left text-sm font-semibold text-primary-800 transition hover:underline"
              >
                View Returns & Exchanges Policy
              </button>
            </div>
          </aside>

          <article className="rounded-2xl border border-[#eadde1] bg-white p-6 sm:p-10">
            <p className="text-sm leading-7 text-slate-600">
              This Shipping Policy applies to orders placed through{" "}
              <a
                href="https://charmscollections.in"
                className="font-semibold text-primary-800 underline underline-offset-4"
              >
                charmscollections.in
              </a>
              . Please read it together with our Terms and Conditions and
              Returns, Refunds and Exchanges Policy.
            </p>

            <div className="mt-10 space-y-10">
              <PolicySection title="1. Serviceable Locations">
                <p>
                  We ship only to serviceable delivery PIN codes shown at
                  checkout. Availability can change based on logistics-partner
                  coverage, product availability, regulatory restrictions,
                  weather or operational conditions.
                </p>
                <p className="mt-4">
                  We may not ship to certain remote, restricted or
                  non-serviceable locations.
                </p>
              </PolicySection>

              <PolicySection title="2. Order Confirmation and Processing">
                <p>
                  CHARMS currently accepts prepaid online orders. An order is
                  not confirmed merely because it has been submitted. We
                  confirm an order after successful payment verification, stock
                  review and acceptance by CHARMS.
                </p>
                <p className="mt-4">
                  Orders with pending, failed, cancelled or unverified payments
                  will not be treated as confirmed orders.
                </p>
                <p className="mt-4">
                  Orders are normally processed within two business days after
                  confirmation, excluding Sundays, public holidays and
                  exceptional peak periods. If an item cannot be fulfilled, we
                  may contact you, offer alternatives, cancel the affected item
                  or issue the applicable refund.
                </p>
              </PolicySection>

              <PolicySection title="3. Delivery Estimates">
                <p>
                  Estimated delivery dates are shown at checkout or communicated
                  after dispatch. They are estimates and not guaranteed delivery
                  commitments.
                </p>
                <p className="mt-4">
                  Delivery time depends on the delivery address, carrier
                  capacity, weather, public holidays, security checks and other
                  factors outside our reasonable control.
                </p>
              </PolicySection>

              <PolicySection title="4. Shipping Charges">
                <p>
                  Shipping charges, free-shipping thresholds and any applicable
                  delivery fees are shown at checkout before payment.
                </p>
                <div className="mt-4 rounded-xl border border-dashed border-primary-200 bg-primary-50/50 p-4 text-sm text-primary-950">
                  Add your standard shipping rule here, for example: free
                  shipping above ₹5,000; otherwise a ₹[amount] delivery fee.
                </div>
              </PolicySection>

              <PolicySection title="5. Tracking and Delivery">
                <p>
                  When an order is dispatched, we will send tracking
                  information to the email address or mobile number associated
                  with the order, where available.
                </p>
                <p className="mt-4">
                  You are responsible for providing a complete and accurate
                  delivery address, reachable contact details and any access
                  instructions needed for delivery.
                </p>
                <p className="mt-4">
                  A delivery partner may attempt delivery more than once
                  according to its process. If delivery fails because of an
                  incorrect address, refusal, repeated unavailability or a
                  failure to respond, the shipment may be returned to us.
                </p>
                <p className="mt-4">
                  Any reshipment, cancellation or refund will be considered
                  after review and may be subject to reasonable charges where
                  allowed by law.
                </p>
              </PolicySection>

              <PolicySection title="6. Delays, Damage and Missing Parcels">
                <p>
                  If your shipment is delayed, please use the tracking link
                  first and then contact us with your order number.
                </p>
                <p className="mt-4">
                  If a parcel appears damaged, tampered with or incomplete,
                  record its condition where practical and notify us promptly
                  with photographs or video.
                </p>
                <p className="mt-4">
                  We will investigate with the relevant carrier and handle any
                  eligible remedy under our Returns, Refunds and Exchanges
                  Policy.
                </p>
              </PolicySection>

              <PolicySection title="7. Address Changes and Cancellation Requests">
                <p>
                  Contact us immediately if you need to change a delivery
                  address or request cancellation. We cannot guarantee changes
                  after an order enters packing or dispatch.
                </p>
                <p className="mt-4">
                  A cancellation request is effective only when expressly
                  confirmed by CHARMS.
                </p>
              </PolicySection>

              <PolicySection title="8. Contact Us">
                <p>
                  For delivery, tracking or shipping-related support, please
                  contact CHARMS using the customer-support details published on
                  our website.
                </p>

                <div className="mt-5 rounded-xl bg-[#faf7f5] p-5 text-sm leading-7 text-slate-600">
                  Before publishing, add CHARMS’ legal entity name, registered
                  address, customer-support email and phone number, plus the
                  grievance officer’s name, designation, email and postal
                  address.
                </div>
              </PolicySection>
            </div>
          </article>
        </div>
      </section>
    </main>
  );
}

function PolicySection({ title, children }) {
  return (
    <section>
      <h2 className="text-xl font-semibold tracking-tight text-slate-950 sm:text-2xl">
        {title}
      </h2>

      <div className="mt-4 text-sm leading-7 text-slate-600 sm:text-[15px]">
        {children}
      </div>
    </section>
  );
}

function SummaryItem({ icon, title, text }) {
  return (
    <div className="flex gap-3">
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary-800">
        {icon}
      </span>

      <div>
        <p className="text-sm font-semibold text-slate-900">{title}</p>
        <p className="mt-1 text-xs leading-5 text-slate-500">{text}</p>
      </div>
    </div>
  );
}